// Top-level layout shell. Composes:
//   1. MasqueradeBanner — amber bar when an admin is viewing as another user
//   2. Sidebar — desktop rail + mobile drawer
//   3. MobileHeader — hamburger trigger below the lg breakpoint
//   4. Main content slot
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DemoTour } from './DemoTour';
import { LayoutDashboard, Phone, BookOpen, Target, BarChart3, HelpCircle, GraduationCap, Award, Link2, Palette, Users, MessageSquare, CalendarCheck, TrendingUp, Fingerprint, Command } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { MasqueradeBanner } from './layout/MasqueradeBanner';
import { Sidebar } from './layout/Sidebar';
import { MobileHeader } from './layout/MobileHeader';
import { TenantBrandMasthead } from './layout/TenantBrandMasthead';
import { BroadcastBanner } from '../BroadcastBanner';
import { BudgetAlertBanner } from './BudgetAlertBanner';
import { SiteScopeBadge } from '../SiteScopeBadge';
import { ForcePasswordChangeModal } from './ForcePasswordChangeModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tenant-facing portal navigation — Supervisor-first 5-domain structure
// (Darren, Jun 2026): Command Center / Team & Agent Hub / Training Library /
// Coaching Lab / Ops & Settings. Hrefs unchanged — grouping + renames only.
const NAV_SECTIONS = [
  { section: 'Command Center', items: [
    { name: 'Command Center', href: '/command-center', icon: Command, managerOnly: true },
    { name: 'Classic Dashboard', href: '/my-domain', icon: LayoutDashboard },
  ] },
  { section: 'Team & Agent Hub', items: [
    { name: 'Agent Hub', href: '/manager/ops', icon: Users, managerOnly: true },
    { name: 'Supervisor Today', href: '/supervisor/today', icon: CalendarCheck, managerOnly: true },
    { name: 'Coaching Records', href: '/manager/coaching-records', icon: Award, managerOnly: true },
    { name: 'Team & Access', href: '/team/users', icon: Users, managerOnly: true },
    { name: 'My Performance', href: '/my/performance', icon: Target },
    { name: 'Achievements', href: '/achievements', icon: Award, agentOnly: true },
  ] },
  { section: 'Training Library', items: [
    { name: 'Training Hub', href: '/training', icon: GraduationCap, requiresTraining: true },
    { name: 'DISC Insights', href: '/disc-insights', icon: Fingerprint },
    { name: 'Knowledge Bases', href: '/knowledge-bases', icon: BookOpen, managerOnly: true },
    { name: 'How Scoring Works', href: '/help', icon: HelpCircle },
  ] },
  { section: 'Coaching Lab', items: [
    { name: 'AI Coached Calls', href: '/calls', icon: Phone },
    { name: 'SMS Inbox', href: '/sms-inbox', icon: MessageSquare, badgeKey: 'sms' },
    { name: 'Live Chat', href: '/livechat', icon: MessageSquare, badgeKey: 'livechat' },
  ] },
  { section: 'Ops & Settings', items: [
    { name: 'GymSales Sync', href: '/gymsales-sync', icon: Link2, managerOnly: true, gymsalesOnly: true },
    { name: 'Revival Pipeline', href: '/club-summary', icon: TrendingUp, managerOnly: true },
    { name: 'Integrations', href: '/integrations', icon: Link2, managerOnly: true },
    { name: 'Branding', href: '/branding', icon: Palette, managerOnly: true },
  ] },
];

// Classic flat navigation — the original pre-Supervisor-Hub format. Used
// by every domain EXCEPT those with domains.ui_layout='supervisor_hub'
// (Total Fusion only, per Darren Oct 2026 parallel-run agreement).
const NAV_CLASSIC = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'My Performance', href: '/my/performance', icon: Target },
  { name: 'Achievements', href: '/achievements', icon: Award, agentOnly: true },
  { name: 'Manager Ops', href: '/manager/ops', icon: BarChart3, managerOnly: true },
  { name: 'Supervisor Today', href: '/supervisor/today', icon: CalendarCheck, managerOnly: true },
  { name: 'Coaching Records', href: '/manager/coaching-records', icon: Award, managerOnly: true },
  { name: 'Calls', href: '/calls', icon: Phone },
  { name: 'SMS Inbox', href: '/sms-inbox', icon: MessageSquare, badgeKey: 'sms' },
  { name: 'GymSales Sync', href: '/gymsales-sync', icon: Link2, managerOnly: true, gymsalesOnly: true },
  { name: 'Live Chat', href: '/livechat', icon: MessageSquare, badgeKey: 'livechat' },
  { name: 'Summary Report', href: '/club-summary', icon: BarChart3, managerOnly: true },
  { name: 'Knowledge Bases', href: '/knowledge-bases', icon: BookOpen, managerOnly: true },
  { name: 'Integrations', href: '/integrations', icon: Link2, managerOnly: true },
  { name: 'Team Users', href: '/team/users', icon: Users, managerOnly: true },
  { name: 'Branding', href: '/branding', icon: Palette, managerOnly: true },
  { name: 'How Scoring Works', href: '/help', icon: HelpCircle },
  { name: 'Training & Retraining', href: '/training', icon: GraduationCap, requiresTraining: true },
];

const Layout = ({ children }) => {
  const { user, token, logout, isMasquerading, masqueradeUser, exitMasquerade, uiLayout, gymsalesEnabled } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Cache the training_program for this session so we can hide the
  // "Training & Retraining" link on tenants where nothing is configured.
  // Refetched whenever the user identity flips (initial login,
  // masquerade start / stop). Initial value `null` (unknown) means we
  // OMIT the link until the fetch resolves — safer than showing +
  // hiding for tenants where training is disabled entirely.
  const [trainingProgram, setTrainingProgram] = useState(null);
  const [chatUnread, setChatUnread] = useState(0);
  const [smsUnread, setSmsUnread] = useState(0);

  useEffect(() => {
    if (!token || !user?.domain_id) return undefined;
    const tick = () => {
      axios
        .get(`${API}/livechat/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setChatUnread(r.data?.count || 0))
        .catch(() => {});
      axios
        .get(`${API}/sms/replies/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setSmsUnread(r.data?.count || 0))
        .catch(() => {});
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [token, user?.domain_id]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/training/mode`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setTrainingProgram(r.data?.program || 'none'))
      .catch(() => setTrainingProgram('none'));
  }, [token, isMasquerading]);

  // Hub domains (TF) get the grouped 5-domain sections; everyone else
  // keeps the classic flat sidebar (parallel-run, TF-only agreement).
  const isHub = uiLayout === 'supervisor_hub';
  const baseSections = isHub ? NAV_SECTIONS : [{ section: null, items: NAV_CLASSIC }];
  const sections = baseSections.map((s) => ({
    ...s,
    items: s.items
      .filter((n) => {
        if (n.requiresTraining) {
          // While loading (null) OR explicitly 'none', hide the link.
          if (trainingProgram === null || trainingProgram === 'none') return false;
        }
        // GymSales Sync only renders for tenants with GymSales enabled (TF).
        if (n.gymsalesOnly && !gymsalesEnabled) return false;
        return true;
      })
      .map((n) => {
        if (n.badgeKey === 'livechat' && chatUnread > 0) return { ...n, badge: chatUnread };
        if (n.badgeKey === 'sms' && smsUnread > 0) return { ...n, badge: smsUnread };
        return n;
      }),
  }));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleExitMasquerade = () => {
    exitMasquerade();
    navigate('/admin/domains');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] tenant-portal-scope">
      {user?.must_change_password && !isMasquerading && (
        <ForcePasswordChangeModal token={token} onLogout={handleLogout} />
      )}
      {isMasquerading && (
        <MasqueradeBanner
          masqueradeUser={masqueradeUser}
          onExit={handleExitMasquerade}
        />
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        user={user}
        sections={sections}
        currentPath={location.pathname}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onLogout={handleLogout}
      />

      <div className={`lg:pl-64 ${isMasquerading ? 'pt-10' : ''}`}>
        <TenantBrandMasthead />
        <MobileHeader onOpen={() => setSidebarOpen(true)} />
        <BudgetAlertBanner />
        <BroadcastBanner />
        <SiteScopeBadge />
        <main className="p-6 md:p-8">{children}</main>
        <DemoTour />
      </div>
    </div>
  );
};

export default Layout;
